import {
  CHART_LABEL,
  CHART_LAYOUT,
  CHART_TEXT,
  LEGEND_SPLIT_THRESHOLD
} from "../recipeImpactChart.constants";
import { ChartSourceData } from "../recipeImpactChart.types";
import {
  buildPatternIndexById,
  collectBreakdownItems,
  collectBreakdownItemsInDiscoveryOrder,
  indexRecipeDefinitionById,
  indexSourceItemsById,
  mergeSourceIndexes,
  resolveColorById
} from "../utils/breakdownItemCollection.utill";
import { buildLegend, buildTitle, buildTooltip } from "./recipeImpactChart.parts.helper";
import {
  buildBreakdownSeries,
  buildTotalImpactSeries,
  buildTotalImpacts
} from "./recipeImpactChart.series.helper";
import { buildVariantsToShow } from "./recipeImpactChart.variants.helper";

/**
 * Assembles the complete ECharts option object for the recipe impact chart.
 * Pure: given the same source data it always returns the same option shape.
 */
export const buildChartOptions = (source: ChartSourceData) => {
  const { recipeResults, originalRecipeData } = source;
  const originalBreakdown = recipeResults?.originalRecipeImpactBreakdown || {};

  const allBreakdowns = [
    originalBreakdown,
    ...(recipeResults?.variantstotalImpacts || []).map(
      variant => variant.variantImpactBreakdown || {}
    )
  ];

  const discoveryOrderItems = collectBreakdownItemsInDiscoveryOrder(allBreakdowns);
  const breakdownItems = collectBreakdownItems(allBreakdowns);
  const colorById = resolveColorById(
    breakdownItems,
    mergeSourceIndexes(
      indexSourceItemsById(allBreakdowns),
      indexRecipeDefinitionById(originalRecipeData?.originalRecipe, originalRecipeData?.variants)
    ),
    discoveryOrderItems
  );
  const patternIndexById = buildPatternIndexById(discoveryOrderItems);
  const seriesNameToBaseColor = Object.fromEntries(
    breakdownItems.map(item => [item.name || CHART_LABEL.unknown, colorById[item.id]])
  );

  const variantsToShow = buildVariantsToShow(source);
  const columnNames = variantsToShow.map(variant => variant.variantName);
  const isSplitLegend = breakdownItems.length > LEGEND_SPLIT_THRESHOLD;

  const series = [
    ...buildBreakdownSeries(breakdownItems, variantsToShow, colorById, patternIndexById),
    buildTotalImpactSeries(buildTotalImpacts(variantsToShow, recipeResults))
  ];

  return {
    title: buildTitle(),
    tooltip: buildTooltip(seriesNameToBaseColor),
    legend: buildLegend(breakdownItems.reverse()),
    grid: {
      top: isSplitLegend
        ? CHART_LAYOUT.gridTopWithSplitLegend
        : CHART_LAYOUT.gridTopWithSingleLegend,
      left: CHART_LAYOUT.gridLeft,
      right: CHART_LAYOUT.gridRight,
      bottom: CHART_LAYOUT.gridBottom,
      containLabel: true
    },
    xAxis: {
      type: "category",
      data: columnNames
    },
    yAxis: {
      type: "value",
      name: CHART_TEXT.yAxisName,
      nameLocation: "middle",
      nameGap: CHART_LAYOUT.yAxisNameGap,
      nameRotate: CHART_LAYOUT.yAxisNameRotate
    },
    series
  };
};
